import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import {
  changePasswordSchema,
  formatTotpSecret,
  MIN_PASSWORD_LENGTH,
  parseOtpAuthUri,
  summarizeSecurity,
  totpCodeSchema,
  type PasskeyDto,
  type SecurityState,
} from "@tc/shared";
import {
  Button,
  Card,
  CardHead,
  CardTitle,
  Field,
  Icon,
  Modal,
  OtpInput,
  PasswordInput,
  PasswordStrength,
  TextInput,
  Toast,
} from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { passkey, twoFactor } from "@/lib/auth/client";
import {
  changeAccountPassword,
  deletePasskey,
  fetchSecurityState,
  listActiveSessions,
  listPasskeys,
  PasswordChangeError,
  revokeOtherSessions,
  revokeSessionByToken,
  type ActiveSession,
} from "../api";

/**
 * Settings → Security (TW-3463, relocated from the standalone TW-3455 page with no behaviour
 * change). Lets a user manage the factors beyond their password: enrol/disable TOTP two-factor
 * (with one-time backup codes) and register/remove WebAuthn passkeys. The ceremonies run through
 * the Better Auth client plugins; this section just orchestrates the steps and reflects the
 * resulting state. It mounts inside the shared SettingsPage shell (both shells via RoleLayout).
 */
export function SecuritySection() {
  const { t } = useTranslation();

  const [state, setState] = useState<SecurityState>({ twoFactorEnabled: false, passkeyCount: 0 });
  const [passkeys, setPasskeys] = useState<PasskeyDto[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }, []);

  const refresh = useCallback(async () => {
    const [s, keys] = await Promise.all([fetchSecurityState(), listPasskeys()]);
    setState(s);
    setPasskeys(keys);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const summary = summarizeSecurity({ ...state, passkeyCount: passkeys.length });

  return (
    <div>
      <div className="set-section-head" style={{ marginBottom: 18 }}>
        <h2>{t("settings.security.heading")}</h2>
        <div className="sub">{t("settings.security.sub")}</div>
      </div>

      <div className={`sec-status ${summary.hasSecondFactor ? "ok" : "warn"}`}>
        <Icon name={summary.hasSecondFactor ? "checkCircle" : "alert"} size={18} />
        <span>{summary.hasSecondFactor ? t("security.statusSecured") : t("security.statusPasswordOnly")}</span>
      </div>

      <PasswordCard flash={flash} />
      <TwoFactorCard enabled={state.twoFactorEnabled} onChange={refresh} flash={flash} />
      <PasskeysCard passkeys={passkeys} onChange={refresh} flash={flash} />
      <SessionsCard flash={flash} />

      {toast && <Toast message={toast} />}
    </div>
  );
}

/* ── Password change (TW-3465) ─────────────────────────────────────────────────────────── */

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}
const EMPTY_PASSWORD_FORM: PasswordForm = { currentPassword: "", newPassword: "", confirmPassword: "" };

/**
 * In-app password change. The three fields each carry a show/hide toggle (kit PasswordInput) and the
 * new password gets a live strength meter. The shared changePasswordSchema gates the submit button
 * (min length + match), and Better Auth verifies the *current* password server-side — a successful
 * change keeps this session valid (we never revoke self), so we just clear the form and flash.
 */
function PasswordCard({ flash }: { flash: (msg: string) => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<PasswordForm>(EMPTY_PASSWORD_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof PasswordForm, string>>>({});
  const [busy, setBusy] = useState(false);

  function set<K extends keyof PasswordForm>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  // The schema (current/new/confirm + match) drives the disabled state, so weak or mismatched
  // passwords block submit before any round-trip.
  const valid = changePasswordSchema.safeParse(form).success;

  async function submit() {
    const result = changePasswordSchema.safeParse(form);
    if (!result.success) {
      const next: Partial<Record<keyof PasswordForm, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof PasswordForm;
        if (!field || next[field]) continue;
        next[field] =
          field === "newPassword"
            ? t("security.password.tooShort", { count: MIN_PASSWORD_LENGTH })
            : field === "confirmPassword"
              ? issue.code === "custom"
                ? t("security.password.mismatch")
                : t("security.password.confirmRequired")
              : t("security.password.currentRequired");
      }
      setErrors(next);
      return;
    }
    setBusy(true);
    try {
      await changeAccountPassword({
        currentPassword: result.data.currentPassword,
        newPassword: result.data.newPassword,
      });
      setForm(EMPTY_PASSWORD_FORM);
      setErrors({});
      flash(t("security.password.changed"));
    } catch (err) {
      if (err instanceof PasswordChangeError && err.reason === "wrongCurrent") {
        setErrors({ currentPassword: t("security.password.wrongCurrent") });
      } else if (err instanceof PasswordChangeError && err.reason === "weak") {
        setErrors({ newPassword: t("security.password.tooShort", { count: MIN_PASSWORD_LENGTH }) });
      } else {
        flash(t("security.password.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead>
        <CardTitle>
          <Icon name="lock" size={16} /> {t("security.password.title")}
        </CardTitle>
      </CardHead>
      <p className="muted" style={{ marginTop: 4 }}>
        {t("security.password.description")}
      </p>

      <div className="form-grid" style={{ marginTop: 16 }}>
        <Field label={t("security.password.current")} full>
          <PasswordInput
            value={form.currentPassword}
            onChange={(v) => set("currentPassword", v)}
            autoComplete="current-password"
            ariaLabel={t("security.password.current")}
          />
          <FieldError msg={errors.currentPassword} />
        </Field>
        <Field label={t("security.password.new")}>
          <PasswordInput
            value={form.newPassword}
            onChange={(v) => set("newPassword", v)}
            autoComplete="new-password"
            ariaLabel={t("security.password.new")}
          />
          {form.newPassword && <PasswordStrength value={form.newPassword} labels={pwLabels(t)} />}
          <FieldError msg={errors.newPassword} />
        </Field>
        <Field label={t("security.password.confirm")}>
          <PasswordInput
            value={form.confirmPassword}
            onChange={(v) => set("confirmPassword", v)}
            autoComplete="new-password"
            ariaLabel={t("security.password.confirm")}
          />
          <FieldError msg={errors.confirmPassword} />
        </Field>
      </div>

      <div className="card-foot">
        <Button variant="primary" icon="check" onClick={submit} disabled={!valid || busy}>
          {t("security.password.submit")}
        </Button>
      </div>
    </Card>
  );
}

/** Localised strength labels indexed by the pure 0–4 passwordStrength score. */
function pwLabels(t: (key: string) => string): [string, string, string, string, string] {
  return [
    t("security.password.strength.0"),
    t("security.password.strength.1"),
    t("security.password.strength.2"),
    t("security.password.strength.3"),
    t("security.password.strength.4"),
  ];
}

/* ── Active sessions (TW-3465) ─────────────────────────────────────────────────────────── */

/**
 * Active-session management. Lists each signed-in device (friendly label + IP + relative last-active),
 * flags the current device "This device" with no revoke action (it can only sign itself out via the
 * topbar), and offers per-session sign-out plus a "sign out everywhere else". Revokes hit Better Auth
 * and invalidate the session server-side; we re-pull the list after each.
 */
function SessionsCard({ flash }: { flash: (msg: string) => void }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setSessions(await listActiveSessions());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // "Sign out everywhere else" only targets live sessions — expired rows are history, not revocable.
  const others = sessions.filter((s) => !s.current && !s.expired);

  async function revokeOne(token: string) {
    setBusy(true);
    try {
      if (await revokeSessionByToken(token)) {
        await load();
        flash(t("security.sessions.signedOut"));
      } else {
        flash(t("security.sessions.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function revokeAllOthers() {
    setBusy(true);
    try {
      if (await revokeOtherSessions()) {
        await load();
        flash(t("security.sessions.signedOutOthers"));
      } else {
        flash(t("security.sessions.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead>
        <CardTitle>
          <Icon name="globe" size={16} /> {t("security.sessions.title")}
          {sessions.length > 0 && <span className="sec-count">{sessions.length}</span>}
        </CardTitle>
      </CardHead>
      <p className="muted" style={{ marginTop: 4 }}>
        {t("security.sessions.description")}
      </p>

      <div className="session-list" style={{ marginTop: 14 }}>
        {sessions.length === 0 ? (
          <div className="sec-empty">{t("security.sessions.none")}</div>
        ) : (
          sessions.map((s) => (
            <div className={s.expired ? "session-row is-expired" : "session-row"} key={s.token}>
              <div className="session-ic">
                <Icon name="globe" size={18} />
              </div>
              <div className="session-body">
                <b>{s.device ?? t("security.sessions.unknownDevice")}</b>
                {s.current && (
                  <span
                    className="tag"
                    style={{ marginLeft: 8, background: "var(--primary-soft)", color: "var(--primary)" }}
                  >
                    {t("security.sessions.thisDevice")}
                  </span>
                )}
                {s.expired && (
                  <span className="tag" style={{ marginLeft: 8 }}>
                    {t("security.sessions.expired")}
                  </span>
                )}
                <div className="se-meta">
                  {[s.ipAddress, fmt.relative(s.lastActive)].filter(Boolean).join(" · ")}
                </div>
              </div>
              {!s.current && !s.expired && (
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => revokeOne(s.token)}>
                  {t("security.sessions.signOut")}
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      {others.length > 0 && (
        <div className="card-foot">
          <Button variant="danger" size="sm" disabled={busy} onClick={revokeAllOthers}>
            {t("security.sessions.signOutOthers")}
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Inline field error (shared shape with ProfileSection's). */
function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <div className="field-err">
      <Icon name="alert" size={13} /> {msg}
    </div>
  );
}

/* ── Two-factor (TOTP) ─────────────────────────────────────────────────────────────────── */

function TwoFactorCard({
  enabled,
  onChange,
  flash,
}: {
  enabled: boolean;
  onChange: () => Promise<void>;
  flash: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);

  return (
    <Card>
      <CardHead>
        <CardTitle>
          <Icon name="lock" size={16} /> {t("security.twoFactor.title")}
        </CardTitle>
        <span className={`sec-pill ${enabled ? "on" : "off"}`}>
          {enabled ? t("security.twoFactor.enabled") : t("security.twoFactor.disabled")}
        </span>
      </CardHead>
      <p className="muted" style={{ marginTop: 4 }}>
        {t("security.twoFactor.description")}
      </p>
      <div style={{ marginTop: 14 }}>
        {enabled ? (
          <Button variant="danger" onClick={() => setDisabling(true)}>
            {t("security.twoFactor.disable")}
          </Button>
        ) : (
          <Button icon="plus" onClick={() => setEnrolling(true)}>
            {t("security.twoFactor.enable")}
          </Button>
        )}
      </div>

      {enrolling && (
        <EnrollTwoFactorModal
          onClose={() => setEnrolling(false)}
          onDone={async () => {
            setEnrolling(false);
            await onChange();
            flash(t("security.twoFactor.enableSuccess"));
          }}
        />
      )}
      {disabling && (
        <DisableTwoFactorModal
          onClose={() => setDisabling(false)}
          onDone={async () => {
            setDisabling(false);
            await onChange();
            flash(t("security.twoFactor.disableSuccess"));
          }}
        />
      )}
    </Card>
  );
}

/** Two-step enrolment: confirm password → show secret + backup codes → verify a code. */
function EnrollTwoFactorModal({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState<{ key: string; account: string | null; uri: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function start() {
    setError(null);
    if (!password) return setError(t("security.twoFactor.passwordRequired"));
    setBusy(true);
    const res = await twoFactor.enable({ password });
    setBusy(false);
    if (res.error || !res.data) {
      return setError(t("security.twoFactor.wrongPassword"));
    }
    const parsed = parseOtpAuthUri(res.data.totpURI);
    setSecret({
      key: parsed ? formatTotpSecret(parsed.secret) : res.data.totpURI,
      account: parsed?.account ?? null,
      uri: res.data.totpURI,
    });
    setBackupCodes(res.data.backupCodes ?? []);
  }

  async function confirm() {
    setError(null);
    if (!totpCodeSchema.safeParse({ code }).success) {
      return setError(t("security.twoFactor.codeError"));
    }
    setBusy(true);
    const res = await twoFactor.verifyTotp({ code: code.trim() });
    setBusy(false);
    if (res.error) return setError(t("security.twoFactor.codeError"));
    await onDone();
  }

  return (
    <Modal open onClose={onClose} title={t("security.twoFactor.enable")} width={480}>
      <div className="modal-body">
        {!secret ? (
          <>
            <p className="muted">{t("security.twoFactor.passwordPrompt")}</p>
            <label className="auth-field-label" htmlFor="tf-pw">
              {t("security.twoFactor.passwordLabel")}
            </label>
            <TextInput type="password" icon="lock" value={password} onChange={setPassword} />
          </>
        ) : (
          <>
            <p className="muted">{t("security.twoFactor.secretHint")}</p>
            <div className="sec-qr">
              <QRCodeSVG value={secret.uri} size={172} marginSize={2} aria-label={t("security.twoFactor.qrAlt")} />
            </div>
            <div className="sec-secret">
              <div className="sec-secret-label">{t("security.twoFactor.secretLabel")}</div>
              <code>{secret.key}</code>
            </div>
            {backupCodes.length > 0 && (
              <div className="sec-backup">
                <div className="sec-backup-head">
                  <Icon name="alert" size={14} /> {t("security.twoFactor.backupTitle")}
                </div>
                <p className="muted">{t("security.twoFactor.backupHint")}</p>
                <div className="sec-backup-grid">
                  {backupCodes.map((bc) => (
                    <code key={bc}>{bc}</code>
                  ))}
                </div>
              </div>
            )}
            <label className="auth-field-label" style={{ marginTop: 12 }}>
              {t("security.twoFactor.codeLabel")}
            </label>
            <OtpInput value={code} onChange={setCode} invalid={!!error} ariaLabel={t("security.twoFactor.codeLabel")} />
          </>
        )}
        {error && (
          <div className="field-err">
            <Icon name="alert" size={13} /> {error}
          </div>
        )}
      </div>
      <div className="modal-foot">
        <Button variant="ghost" onClick={onClose}>
          {t("actions.cancel")}
        </Button>
        {!secret ? (
          <Button onClick={start} disabled={busy}>
            {t("actions.next")}
          </Button>
        ) : (
          <Button onClick={confirm} disabled={busy}>
            {t("security.twoFactor.verifyEnable")}
          </Button>
        )}
      </div>
    </Modal>
  );
}

function DisableTwoFactorModal({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    if (!password) return setError(t("security.twoFactor.passwordRequired"));
    setBusy(true);
    const res = await twoFactor.disable({ password });
    setBusy(false);
    if (res.error) return setError(t("security.twoFactor.wrongPassword"));
    await onDone();
  }

  return (
    <Modal open onClose={onClose} title={t("security.twoFactor.disable")} width={440}>
      <div className="modal-body">
        <p className="muted">{t("security.twoFactor.disableConfirm")}</p>
        <label className="auth-field-label" htmlFor="tf-dpw">
          {t("security.twoFactor.passwordLabel")}
        </label>
        <TextInput type="password" icon="lock" value={password} onChange={setPassword} />
        {error && (
          <div className="field-err">
            <Icon name="alert" size={13} /> {error}
          </div>
        )}
      </div>
      <div className="modal-foot">
        <Button variant="ghost" onClick={onClose}>
          {t("actions.cancel")}
        </Button>
        <Button variant="danger" onClick={submit} disabled={busy}>
          {t("security.twoFactor.disable")}
        </Button>
      </div>
    </Modal>
  );
}

/* ── Passkeys (WebAuthn) ───────────────────────────────────────────────────────────────── */

function PasskeysCard({
  passkeys,
  onChange,
  flash,
}: {
  passkeys: PasskeyDto[];
  onChange: () => Promise<void>;
  flash: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [adding, setAdding] = useState(false);

  async function remove(id: string) {
    if (await deletePasskey(id)) {
      await onChange();
      flash(t("security.passkeys.removed"));
    }
  }

  return (
    <Card>
      <CardHead>
        <CardTitle>
          <Icon name="lock" size={16} /> {t("security.passkeys.title")}
        </CardTitle>
        <Button size="sm" icon="plus" onClick={() => setAdding(true)}>
          {t("security.passkeys.add")}
        </Button>
      </CardHead>
      <p className="muted" style={{ marginTop: 4 }}>
        {t("security.passkeys.description")}
      </p>

      {passkeys.length === 0 ? (
        <div className="sec-empty">{t("security.passkeys.none")}</div>
      ) : (
        <ul className="sec-key-list">
          {passkeys.map((pk) => (
            <li key={pk.id} className="sec-key-row">
              <div className="sec-key-ic">
                <Icon name="lock" size={16} />
              </div>
              <div className="sec-key-meta">
                <div className="sec-key-name">{pk.name || t("security.passkeys.unnamed")}</div>
                <div className="muted sec-key-date">
                  {t("security.passkeys.registered", { date: fmt.date(pk.createdAt) })}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(pk.id)}>
                {t("security.passkeys.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <AddPasskeyModal
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await onChange();
            flash(t("security.passkeys.added"));
          }}
        />
      )}
    </Card>
  );
}

function AddPasskeyModal({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    setBusy(true);
    // addPasskey triggers the browser WebAuthn ceremony; it resolves to an error object (or
    // undefined if the user dismisses the native prompt) rather than throwing.
    const res = await passkey.addPasskey({ name: name.trim() || undefined });
    setBusy(false);
    if (res?.error) return setError(t("security.passkeys.addError"));
    await onDone();
  }

  return (
    <Modal open onClose={onClose} title={t("security.passkeys.addTitle")} width={440}>
      <div className="modal-body">
        <p className="muted">{t("security.passkeys.addHint")}</p>
        <label className="auth-field-label" htmlFor="pk-name">
          {t("security.passkeys.nameLabel")}
        </label>
        <TextInput value={name} onChange={setName} placeholder={t("security.passkeys.namePlaceholder")} />
        {error && (
          <div className="field-err">
            <Icon name="alert" size={13} /> {error}
          </div>
        )}
      </div>
      <div className="modal-foot">
        <Button variant="ghost" onClick={onClose}>
          {t("actions.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy}>
          {t("security.passkeys.create")}
        </Button>
      </div>
    </Modal>
  );
}
